/**
 * useTeamPipeline — Phase 6 Plan 01 Task 3.
 *
 * TanStack Query hook for GET /api/main/team/pipeline-by-rep (the Sales-Manager
 * "Team" tab pipeline-by-rep table, MGR-01/MGR-04).
 *
 * LAZY: `enabled` is gated by the `active` arg (the caller passes
 * `activeTab === 'team'`). Combined with the default staleTime this dedups the
 * SF-quota-burning navigation flurries that RESEARCH Pitfall 4 warns about —
 * flipping between tabs does not re-fire the live SOQL within the stale window.
 *
 * A 401 bounces to SF OAuth via apiFetch (path starts with /api/main/); a 502
 * surfaces as SalesforceUnavailableError → the UX-07 SF-unreachable banner.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '../lib/api-client.js';

import type { FreshnessPill } from '@saledash/shared';

/**
 * One pipeline-by-rep aggregate row (team-pipeline-by-rep.soql projection).
 * USD-only org → openAmount is SUM(Amount); may be null for a rep with no
 * amount rows. Defined locally (not imported from the shared barrel) to keep
 * SPA imports type-only and avoid the Node-only shared-graph Rollup pitfall.
 */
export interface TeamPipelineByRepRow {
  OwnerId: string;
  ownerName: string;
  ownerEmail: string;
  openAmount: number | null;
  oppCount: number;
}

export interface TeamPipelineResponse {
  rows: TeamPipelineByRepRow[];
  freshness: FreshnessPill;
}

interface UseTeamPipelineOptions {
  /** Gate the query to the active tab (caller passes activeTab === 'team'). */
  active: boolean;
}

export function useTeamPipeline(
  { active }: UseTeamPipelineOptions,
): UseQueryResult<TeamPipelineResponse> {
  return useQuery<TeamPipelineResponse>({
    queryKey: ['main', 'team', 'pipeline-by-rep'],
    queryFn: ({ signal }) =>
      apiFetch<TeamPipelineResponse>('/api/main/team/pipeline-by-rep', { signal }),
    enabled: active,
    staleTime: 5 * 60_000,
    retry: 2,
  });
}

// ─── Phase 6 Plan 03 — Team tab manager drilldowns (MGR-02 / MGR-03) ─────────
// All three hooks are LAZY (enabled: active) and reuse the Plan 01 staleTime so
// tab-flipping does not re-fire the live SOQL within the stale window (Pitfall 4).
// Row shapes are defined locally (never imported from the shared barrel) to keep
// SPA imports type-only and dodge the Node-only shared-graph Rollup pitfall.

/**
 * One forecast-by-category row (team-forecast-by-category endpoint, MGR-02).
 * `forecastPosition` is the verbatim custom Forecast_Position__c picklist value
 * (Commit / Best Case / Pipeline / Most Likely) or null for the retained
 * no-position row (rendered "(no position)").
 */
export interface TeamForecastByCategoryRow {
  OwnerId: string;
  ownerName: string;
  forecastPosition: string | null;
  amount: number | null;
  oppCount: number;
}

export interface TeamForecastByCategoryResponse {
  rows: TeamForecastByCategoryRow[];
  freshness: FreshnessPill;
}

/** One win-rate-by-rep row (MGR-03). winRate is null when won+lost === 0. */
export interface TeamWinrateByRepRow {
  OwnerId: string;
  ownerName: string;
  won: number;
  lost: number;
  winRate: number | null;
}

export interface TeamWinrateByRepResponse {
  rows: TeamWinrateByRepRow[];
  freshness: FreshnessPill;
}

/** One cycle-time + ACV row (MGR-03). Both null when the rep has no Closed-Won deals. */
export interface TeamCycleAcvByRepRow {
  OwnerId: string;
  ownerName: string;
  avgCycleDays: number | null;
  avgAcv: number | null;
  dealCount: number;
}

export interface TeamCycleAcvByRepResponse {
  rows: TeamCycleAcvByRepRow[];
  freshness: FreshnessPill;
}

export function useTeamForecastByCategory(
  { active }: UseTeamPipelineOptions,
): UseQueryResult<TeamForecastByCategoryResponse> {
  return useQuery<TeamForecastByCategoryResponse>({
    queryKey: ['main', 'team', 'forecast-by-category'],
    queryFn: ({ signal }) =>
      apiFetch<TeamForecastByCategoryResponse>('/api/main/team/forecast-by-category', { signal }),
    enabled: active,
    staleTime: 5 * 60_000,
    retry: 2,
  });
}

export function useTeamWinrateByRep(
  { active }: UseTeamPipelineOptions,
): UseQueryResult<TeamWinrateByRepResponse> {
  return useQuery<TeamWinrateByRepResponse>({
    queryKey: ['main', 'team', 'winrate-by-rep'],
    queryFn: ({ signal }) =>
      apiFetch<TeamWinrateByRepResponse>('/api/main/team/winrate-by-rep', { signal }),
    enabled: active,
    staleTime: 5 * 60_000,
    retry: 2,
  });
}

export function useTeamCycleAcvByRep(
  { active }: UseTeamPipelineOptions,
): UseQueryResult<TeamCycleAcvByRepResponse> {
  return useQuery<TeamCycleAcvByRepResponse>({
    queryKey: ['main', 'team', 'cycle-acv-by-rep'],
    queryFn: ({ signal }) =>
      apiFetch<TeamCycleAcvByRepResponse>('/api/main/team/cycle-acv-by-rep', { signal }),
    enabled: active,
    staleTime: 5 * 60_000,
    retry: 2,
  });
}
